import { useState, useEffect } from 'react';
import { Input, Button, notification } from 'antd';
import { StepBackwardOutlined } from '@ant-design/icons';
import { useAppDispatch } from 'Redux/store';
import { getDriver } from 'Redux/Driver/reducer';
import { getOrder } from 'Redux/Order/reducer';
import { useSelector } from 'react-redux';
import {
  selectLoadLoading,
  selectLoadTransitStatuses,
  resetTransitStatuses,
} from 'Redux/Load/slice';
import { selectCurrentOrder } from 'Redux/Order/slice';
import { selectCurrentDriver, resetDrivers } from 'Redux/Driver/slice';
import { setNextLoadStatus, setPrevLoadStatus } from 'Redux/Load/reducer';
import { formatDate, capitalizeFirstLetter } from 'Shared/Utils';
import { ILoadStatuses } from 'Shared/Types/load';
import styles from './LoadTransitStatusesForm.module.scss';

type Statuses = {
  currentStatus: string;
  prevStatus: string;
  nextStatus: string;
};

type FormData = {
  transitStatuses: Statuses;
  locationUpdatedAt: string;
  lastLocation: string;
};

export const LoadTransitStatusesForm = () => {
  const dispatch = useAppDispatch();
  const [notificationAPI, contextHolder] = notification.useNotification();
  const transitStatuses = useSelector(selectLoadTransitStatuses);
  const currentLoad = useSelector(selectCurrentOrder);
  const currentDriver = useSelector(selectCurrentDriver);
  const isLoading = useSelector(selectLoadLoading);
  const [rollbackBtnDisabled, setRollbackBtnDisabled] = useState(false);
  const [goBtnDisabled, setGoBtnDisabled] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    transitStatuses: {
      currentStatus: '',
      nextStatus: '',
      prevStatus: '',
    },
    locationUpdatedAt: '',
    lastLocation: '',
  });

  const getLoadStatuses = (loadStatuses: ILoadStatuses): Statuses => {
    let currentStatus = loadStatuses?.current_status || '';
    currentStatus = currentStatus.toLowerCase();
    let prevStatus = loadStatuses?.previous_status || '';
    prevStatus = prevStatus.toLowerCase();
    let nextStatus = loadStatuses?.next_status || '';
    nextStatus = nextStatus.toLowerCase();

    return {
      currentStatus,
      prevStatus,
      nextStatus,
    };
  };

  const handleSubmit = (event: React.FormEvent) => {
    const nativeEvent = event.nativeEvent as SubmitEvent;
    const submitter = nativeEvent.submitter as HTMLButtonElement;
    const btnName = submitter.name;

    if (currentLoad) {
      const updateLoadStatusPromise =
        btnName === 'next'
          ? setNextLoadStatus({ order_id: currentLoad.id })
          : setPrevLoadStatus({ order_id: currentLoad.id });

      dispatch(updateLoadStatusPromise).then((action) => {
        if (action.payload) {
          notificationAPI.success({
            message: 'Load status was updated',
            duration: 2,
            placement: 'top',
          });
        } else {
          notificationAPI.error({
            message: "Internal error, load status wasn't updated",
            duration: 4,
            placement: 'top',
          });
        }
      });
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetTransitStatuses());
      dispatch(resetDrivers());
    };
  }, []);

  useEffect(() => {
    if (currentLoad) {
      const { currentStatus, prevStatus, nextStatus } = getLoadStatuses(
        currentLoad?.my_load_status,
      );
      let loadIsRefused =
        String(currentLoad.order_status).toLowerCase() === 'refused';

      setFormData((prevState) => ({
        ...prevState,
        transitStatuses: {
          currentStatus,
          prevStatus,
          nextStatus,
        },
      }));

      dispatch(getDriver(currentLoad?.letter?.driver_id || -1));

      if (loadIsRefused) {
        setRollbackBtnDisabled(true);
        setGoBtnDisabled(true);
        setSubmitDisabled(true);
        return;
      }

      if (prevStatus === '') {
        setRollbackBtnDisabled(true);
      }

      if (nextStatus === 'completed') {
        setGoBtnDisabled(true);
      }
    }

    const intervalID: ReturnType<typeof setInterval> = setInterval(() => {
      dispatch(getDriver(currentLoad?.letter?.driver_id || -1));
    }, 1000 * 60 * 5);

    return () => {
      clearInterval(intervalID);
    };
  }, [currentLoad, dispatch]);

  useEffect(() => {
    if (currentDriver && currentDriver.vehicle) {
      const { vehicle } = currentDriver;

      setFormData((prevState) => ({
        ...prevState,
        lastLocation: vehicle?.location_from,
        locationUpdatedAt: formatDate({
          date: vehicle?.updated_at,
          hour12: false,
        }).replace(/\/(\d{4})/, ' $1'),
      }));
    }
  }, [currentDriver, dispatch]);

  useEffect(() => {
    if (transitStatuses) {
      const { currentStatus, prevStatus, nextStatus } =
        getLoadStatuses(transitStatuses);

      if (['unloaded', 'delivered', 'paid off'].includes(currentStatus) && currentLoad) {
        dispatch(getOrder(currentLoad?.id));
      }

      setFormData((prevState) => ({
        ...prevState,
        transitStatuses: {
          currentStatus,
          prevStatus,
          nextStatus,
        },
      }));
    }
  }, [transitStatuses]);

  return (
    <form
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();

        if (submitDisabled) {
          return;
        }

        setRollbackBtnDisabled(false);
        setGoBtnDisabled(false);
        setSubmitDisabled(true);

        setTimeout(() => {
          setSubmitDisabled(false);
        }, 1000);

        handleSubmit(event);
      }}
      className={styles.form}
    >
      {contextHolder}
      <label className={styles.form__label} htmlFor="prev">
        Current status
      </label>
      <Input
        id="prev"
        className={styles.form__input}
        value={
          isLoading
            ? 'Loading...'
            : capitalizeFirstLetter(formData.transitStatuses.currentStatus)
        }
        size="small"
        onChange={() => {}}
      />

      <label className={styles.form__label} htmlFor="timestamp">
        Location Updated
      </label>
      <Input
        id="timestamp"
        className={styles.form__input}
        value={
          isLoading
            ? 'Loading...'
            : formData.locationUpdatedAt
        }
        size="small"
        onChange={() => {}}
      />

      <label className={styles.form__label} htmlFor="location">
        Last location
      </label>
      <Input
        id="location"
        className={styles.form__input}
        value={
          isLoading ? 'Loading...' : formData.lastLocation
        }
        size="small"
        onChange={() => {}}
      />

      <div className={styles.form__controls}>
        <Button
          className={`${styles.form__btn} ${styles.form__btn_danger}`}
          htmlType="submit"
          name="previous"
          disabled={
            Boolean(currentLoad) === false ||
            formData.transitStatuses.prevStatus === '' ||
            isLoading ||
            rollbackBtnDisabled
          }
          type="primary"
          danger
          title={
            formData.transitStatuses.prevStatus && submitDisabled === false
              ? 'Rollback the load status'
              : ''
          }
        >
          <StepBackwardOutlined />
        </Button>

        <Button
          className={styles.form__btn}
          htmlType="submit"
          name="next"
          disabled={
            Boolean(currentLoad) === false ||
            formData.transitStatuses.nextStatus === 'completed' ||
            isLoading ||
            goBtnDisabled
          }
          type="primary"
          title={
            formData.transitStatuses.nextStatus === 'completed' ||
            submitDisabled
              ? ''
              : `Next status: ${capitalizeFirstLetter(
                  formData.transitStatuses.nextStatus,
                )}`
          }
        >
          <span className={styles.form__btn__content}>
            {formData.transitStatuses.nextStatus
              ? capitalizeFirstLetter(formData.transitStatuses.nextStatus)
              : 'Next status unavailable'}
          </span>
        </Button>
      </div>
    </form>
  );
};
